import api from './api';
import type { ApiResponse } from '@/types/api.types';
import type { WatcherStatus } from '@/types/enums.generated';

/**
 * Watcher registry — the console's management of watcher clients
 * (/api/v1/watchers, ADR-034). Renamed from file-watcher.service.ts in
 * ADR-044 so the name no longer collides with the deleted watcher.service.ts
 * (the LAN-direct agent client). Restore requests live in restore.service.ts.
 */
export interface WatcherClient {
  id: string;
  name: string;
  description?: string;
  api_key_hint?: string;
  is_enabled: boolean;
  last_seen_at?: string;
  /** derived by the backend (ADR-044 D2); see utils/watcher.ts */
  status: WatcherStatus;
  /** reported by the agent's commands poll; absent for agents that predate ADR-044 */
  agent_version?: string;
  /** the agent's own /health degraded_reason (auth_pending, watcher_disabled, …) */
  degraded_reason?: string;
  /** the watcher's group (ADR-033 provenance); absent when it has none */
  group_id?: string;
  /** group name and creating account (QA #100), filled by list / get */
  group_name?: string;
  owner_username?: string;
  created_at: string;
  updated_at?: string;
}

export interface CreateWatcherRequest {
  name: string;
  description?: string;
}

interface CreateWatcherResponse {
  id: string;
  name: string;
  description?: string;
  api_key: string;       // Full key, shown once only
  api_key_hint: string;  // Masked version
  is_enabled: boolean;
  created_at: string;
  /**
   * Bundle download token (INFRA-ADR-025). Present if backend is configured with
   * FILEWATCHER_ARTIFACTS_URL, empty string otherwise. Pass to
   * downloadBundle(id, token) within 5 minutes of receiving it.
   */
  bundle_token?: string;
}

export interface UpdateWatcherRequest {
  name?: string;
  description?: string;
}

interface RegenerateKeyResponse {
  id: string;
  name: string;
  api_key: string;
  api_key_hint: string;
  /** Same semantics as CreateWatcherResponse.bundle_token (INFRA-ADR-025). */
  bundle_token?: string;
}

export const watcherRegistryService = {
  async listWatchers(): Promise<WatcherClient[]> {
    const response = await api.get<ApiResponse<WatcherClient[]>>('/api/v1/watchers');
    return response.data.data || [];
  },

  async createWatcher(data: CreateWatcherRequest): Promise<CreateWatcherResponse> {
    const response = await api.post<ApiResponse<CreateWatcherResponse>>('/api/v1/watchers', data);
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error('Failed to create watcher');
  },

  async updateWatcher(id: string, data: UpdateWatcherRequest): Promise<WatcherClient> {
    const response = await api.put<ApiResponse<WatcherClient>>(`/api/v1/watchers/${id}`, data);
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error('Failed to update watcher');
  },

  async deleteWatcher(id: string): Promise<void> {
    const response = await api.delete<ApiResponse>(`/api/v1/watchers/${id}`);
    if (!response.data.success) throw new Error('Failed to delete watcher');
  },

  async enableWatcher(id: string): Promise<WatcherClient> {
    const response = await api.post<ApiResponse<WatcherClient>>(`/api/v1/watchers/${id}/enable`);
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error('Failed to enable watcher');
  },

  async disableWatcher(id: string): Promise<WatcherClient> {
    const response = await api.post<ApiResponse<WatcherClient>>(`/api/v1/watchers/${id}/disable`);
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error('Failed to disable watcher');
  },

  async regenerateKey(id: string): Promise<RegenerateKeyResponse> {
    const response = await api.post<ApiResponse<RegenerateKeyResponse>>(`/api/v1/watchers/${id}/regenerate-key`);
    if (response.data.success && response.data.data) return response.data.data;
    throw new Error('Failed to regenerate key');
  },

  /**
   * INFRA-ADR-025: download a pre-built install bundle (tar.gz containing
   * install.sh with API key embedded + file-watcher binary).
   * Token TTL is 5 minutes on the backend; can be called repeatedly within
   * that window. Returns 410 Gone after expiry; caller should regenerate key.
   */
  async downloadBundle(watcherId: string, bundleToken: string): Promise<void> {
    const response = await api.get<Blob>(`/api/v1/watchers/${watcherId}/bundle`, {
      params: { token: bundleToken },
      responseType: 'blob',
      // Same as fileService.downloadFile: the 30s instance timeout bounds the
      // whole transfer in the browser, so a slow link fails the bundle mid-way.
      timeout: 0,
    });

    // Extract filename from Content-Disposition header if present.
    const cd = response.headers['content-disposition'] || '';
    const match = /filename="?([^"]+)"?/i.exec(cd);
    const filename = match?.[1] || `file-watcher-installer-${watcherId}.tar.gz`;

    // Trigger browser download.
    const url = URL.createObjectURL(response.data);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
};
